import { useEffect, useState } from 'react';

type AdminTheme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'adm-theme';

interface UseAdminThemeReturn {
  theme: AdminTheme;
  isDark: boolean;
  setTheme: (theme: AdminTheme) => void;
  toggleTheme: () => void;
}

/**
 * Light/dark theme for the admin panel only. Applies `data-theme` to
 * `.adm-shell` (via the returned isDark flag, consumed by AdminLayout) —
 * never to <html> — so it can't affect the rest of the app, unlike the
 * app-wide (and currently unmounted) useTheme/ThemeProvider pair. Persists
 * under its own localStorage key, separate from the app-wide `theme` key.
 */
export function useAdminTheme(): UseAdminThemeReturn {
  const [theme, setThemeState] = useState<AdminTheme>('system');
  const [isDark, setIsDark] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    const stored = (localStorage.getItem(STORAGE_KEY) as AdminTheme | null) || 'system';
    setThemeState(stored);
    const shouldBeDark =
      stored === 'dark' ||
      (stored === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    setIsDark(shouldBeDark);
  }, []);

  useEffect(() => {
    if (theme !== 'system') return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => setIsDark(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  function setTheme(newTheme: AdminTheme) {
    setThemeState(newTheme);
    localStorage.setItem(STORAGE_KEY, newTheme);
    setIsDark(
      newTheme === 'dark' ||
        (newTheme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
    );
  }

  function toggleTheme() {
    setTheme(isDark ? 'light' : 'dark');
  }

  if (!isMounted) {
    return { theme: 'system', isDark: false, setTheme: () => {}, toggleTheme: () => {} };
  }

  return { theme, isDark, setTheme, toggleTheme };
}
